/**
 * Заголовок группы партий таблицы планов: поставщик, дата, QR-папка и её
 * файлы, техкарты, оплата, итог заказа и итог папки. Вынесено из
 * `components/PlansTable.tsx` (нарезка 14.10.26).
 */

import { ChevronRight, FileImage, QrCode } from "lucide-react";
import { useContext } from "react";
import { Link } from "react-router";
import { DownloadLink } from "@/components/DownloadLink";
import { PlanDownloadMenu } from "@/components/PlanDownloadMenu";
import { SupplierName } from "@/components/SupplierName";
import { WechatSend } from "@/components/WechatSend";
import { nf } from "@/lib/format";
import { bqdHref } from "@/lib/orders";
import {
  groupAmount,
  groupModels,
  groupPaidMark,
  type PlanGroup,
  plural,
  qrPngHref,
  qrProblem,
  techcardHref,
} from "@/lib/plans";
import { выделяли, клавишаКнопки } from "@/lib/selection";
import type { WechatState } from "@/lib/wechat";
import { Badge } from "./Badge";
import { PickBox, type PickState, ТолькоЧтение } from "./PickBox";
import { fmtDate } from "./format";
import { MARK_TONE } from "./style";

export function GroupRow({
  group,
  wechat,
  onWechat,
  groupQr,
  open,
  pick,
  onPick,
  onToggle,
}: {
  group: PlanGroup;
  wechat: WechatState | undefined;
  onWechat: (state: WechatState) => void;
  groupQr: { blocked: string | null; busy: boolean; create: (group: PlanGroup) => void } | undefined;
  open: boolean;
  pick: PickState;
  onPick: (on: boolean) => void;
  onToggle: () => void;
}) {
  const planned = group.plans.reduce((sum, plan) => sum + plan.qty_planned, 0);
  const ordered = group.plans.reduce((sum, plan) => sum + plan.qty_ordered, 0);
  const models = groupModels(group);
  const amount = groupAmount(group);
  const withCard = group.plans.some((plan) => plan.has_card);
  const readOnly = useContext(ТолькоЧтение);
  return (
    // `div role="button"`, а НЕ `<button>`: внутри стоит чекбокс, а контрол
    // внутри кнопки — невалидная разметка, и клик браузер отдаёт кнопке.
    // Клавиатура и «не сворачивать после выделения» — из `lib/selection`.
    <div
      role="button"
      tabIndex={0}
      onClick={() => {
        if (!выделяли()) onToggle();
      }}
      onKeyDown={клавишаКнопки(onToggle)}
      data-annot={`группа ${group.supplier}`}
      className="flex h-[34px] w-full cursor-pointer items-center gap-2 border-y border-border bg-bg-subtle pr-2.5 text-left text-[12.5px] text-fg-muted"
    >
      <PickBox state={pick} title="отметить все партии группы" onChange={onPick} />
      <ChevronRight
        size={14}
        className={
          "flex-none transition-transform " + (open ? "rotate-90 text-fg" : "")
        }
      />
      {/* Имя поставщика первым, и это не косметика: список читают вопросом
          «что уходит этой фабрике», а не «что лежит в папке 686». */}
      <SupplierName id={group.supplierId} name={group.supplier} className="flex-none font-medium text-fg" />
      {/* Дата и у группы с папкой (замечание владельца 23.09.26): по ней
          отличают два заказа одной фабрике, собранные в одну папку. */}
      <span className="tnum flex-none text-fg">{fmtDate(group.date)}</span>
      {group.qrId ? (
        // Ссылка ведёт на НАШУ карточку папки — сверку план → заказ → оплата
        // (разметка 28.09.26 bqd-720); в CRM папка открывается с неё же.
        // `stopPropagation` — переход по ссылке не должен сворачивать группу.
        // Клавиши гасятся тоже: Enter иначе дошёл бы до `клавишаКнопки`
        // заголовка, тот сделал бы `preventDefault` — и ссылка с клавиатуры не
        // открылась бы вовсе, а группа свернулась (финальное ревью 15.09.26).
        <Link
          to={bqdHref(group.qrId)}
          title="карточка QR-папки: сверка плана и заказа"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          className="flex-none whitespace-nowrap rounded border border-border-strong px-1.5 font-mono text-[11.5px] text-fg hover:border-accent hover:text-accent-text"
        >
          QR {group.qrId}
        </Link>
      ) : null}
      {group.qrId ? (
        // Картинка папки и файлы всей папки (спека …-plans-export-smart, части
        // 2–3) — свои ручки, не CRM. Клики и клавиши гасятся тем же доводом,
        // что у ссылки на папку: группа под значком сворачиваться не должна.
        <>
          <a
            href={qrPngHref(group.qrId)}
            target="_blank"
            rel="noreferrer"
            title={`картинка QR-папки ${group.qrId}`}
            aria-label={`картинка QR-папки ${group.qrId}`}
            data-annot={`картинка QR ${group.qrId}`}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            className="flex h-[21px] flex-none items-center rounded border border-border-strong px-1 text-fg-muted hover:border-accent hover:text-accent-text"
          >
            <QrCode className="h-3.5 w-3.5" />
          </a>
          <PlanDownloadMenu qr={group.qrId} compact />
          {readOnly ? null : <WechatSend qrId={group.qrId} state={wechat} onState={onWechat} />}
        </>
      ) : groupQr && !readOnly ? (
        <ПапкаДляГруппы group={group} groupQr={groupQr} />
      ) : (
        <span className="truncate text-fg-faint">· без QR-папки</span>
      )}
      {withCard ? (
        // Техкарты всей группы одним архивом (разметка 25.09.26 15:28 п.3): у
        // папки — вся папка, как у «скачать»; без папки — партии группы.
        <DownloadLink
          href={techcardHref(group.qrId ? { qr: group.qrId } : { ids: group.plans.map((plan) => plan.id) })}
          title="техкарты группы архивом, с подписью, как в CRM"
          aria-label="техкарты группы"
          data-annot={`техкарты ${group.qrId ?? group.supplier}`}
          className="flex h-[21px] flex-none items-center rounded border border-border-strong px-1 text-fg-muted hover:border-accent hover:text-accent-text"
        >
          <FileImage className="h-3.5 w-3.5" />
        </DownloadLink>
      ) : null}
      {/* Платёж в CRM висит на ПАПКЕ, поэтому его метка стоит на группе, а не
          повторяется двадцатью одинаковыми строками внутри. У группы без папки
          метки нет вовсе — там и вопроса нет. */}
      <МеткаОплатыГруппы group={group} />
      {/* Итог заказа фабрике (разметка 25.09.26 15:28 п.2): штуки и модели —
          «1000 штук платья — это 20 моделей»; размеры владелец не просил. */}
      {/* Сумма по плану (разметка 25.09.26 15:55 п.2) — рядом с оплатой, чтобы
          «сколько стоит» и «сколько заплачено» читались вместе. Число партий
          убрано там же: заказ фабрике читают штуками и моделями. Итог стоит
          сразу за оплатой, а не прижат вправо (разметка 25.09.26 16:09): на
          широком экране взгляд до правого края не доходит. */}
      <span className="tnum flex-none text-[11.5px] text-fg-faint">
        <span
          className="text-fg"
          title={
            amount.noPrice > 0
              ? `сумма по плану, юани; без цены ${nf.format(amount.noPrice)} стр. — сумма занижена`
              : "сумма по плану, юани"
          }
        >
          план ¥ {nf.format(Math.round(amount.sum))}
          {amount.noPrice > 0 ? <span className="text-err">*</span> : null}
        </span>{" "}
        · <span className="text-fg">{nf.format(planned)} шт</span> ·{" "}
        <span className="text-fg">
          {models} {plural(models, ["модель", "модели", "моделей"])}
        </span>{" "}
        · в заказе {nf.format(ordered)}
      </span>
      <ИтогПапки group={group} />
    </div>
  );
}

/**
 * Итог ВСЕЙ папки мимо отбора (разметка 28.09.26 bqd-720): под «Активным»
 * остаются незаказанные партии, и «в заказе 0» группы прятало, что остальная
 * папка уже уехала. Числа — те же, что на карточке `/bqd/:id`.
 */
function ИтогПапки({ group }: { group: PlanGroup }) {
  const folder = group.folder;
  if (!folder || folder.plan === 0) return null;
  const всё = folder.ordered >= folder.plan;
  return (
    <span
      className="tnum flex-none text-[11.5px] text-fg-faint"
      title={`вся папка QR ${group.qrId}, без отбора экрана: заказано ${nf.format(folder.ordered)} из ${nf.format(folder.plan)} шт по плану`}
    >
      · папка: в заказе{" "}
      <span className={всё ? "text-ok" : "text-fg"}>
        {nf.format(folder.ordered)} из {nf.format(folder.plan)}
      </span>
    </span>
  );
}

/**
 * «без QR-папки» кнопкой (разметка 28.09.26 12:59 п.1): клик — папка для ВСЕХ
 * партий группы, какими их показывает отбор; подтверждение спрашивает
 * страница. Застава — та же `qrProblem`, что у кнопки полосы выбора: закрыта —
 * причина в подсказке, клик не пишет. Клики и клавиши гасятся, как у ссылки на
 * папку: группа под кнопкой сворачиваться не должна.
 */
function ПапкаДляГруппы({
  group,
  groupQr,
}: {
  group: PlanGroup;
  groupQr: { blocked: string | null; busy: boolean; create: (group: PlanGroup) => void };
}) {
  const why = groupQr.busy ? "запись идёт" : qrProblem(group.plans, groupQr.blocked);
  return (
    <button
      type="button"
      disabled={why !== ""}
      title={why || `создать QR-папку для ${group.plans.length} ${plural(group.plans.length, ["партии", "партий", "партий"])} группы`}
      data-annot={`создать QR ${group.supplier}`}
      onClick={(event) => {
        event.stopPropagation();
        groupQr.create(group);
      }}
      onKeyDown={(event) => event.stopPropagation()}
      className="flex-none truncate rounded border border-dashed border-border-strong px-1.5 text-[11.5px] text-fg-faint enabled:cursor-pointer enabled:hover:border-accent enabled:hover:text-accent-text disabled:cursor-not-allowed"
    >
      · без QR-папки
    </button>
  );
}

/** Оплата группы — «оплачено ¥ N» / «не оплачено»; у группы без QR-папки метки нет. */
function МеткаОплатыГруппы({ group }: { group: PlanGroup }) {
  const mark = groupPaidMark(group);
  if (mark === null) return null;
  return <Badge tone={MARK_TONE[mark.tone]} text={mark.text} title={mark.title} />;
}
